import React from 'react'
import tw from 'twin.macro'
import styled from 'styled-components'
import { Currency, Pair } from '@nominex/nomiswap-sdk'
import { SlButton } from '@shoelace-style/shoelace/dist/react'
import { ArrowDropDownIcon, Text, useModal } from '@nominex/dex-uikit'
import { CurrencyImage, CurrencyPairImage } from 'components/CurrencyImage'
import { useTranslation } from 'contexts/Localization'
import { useWagmi } from 'libs/wagmi'
import { useCurrencyBalance } from 'state/wallet/hooks'
import CurrencySearchModal from '../SearchModal/CurrencySearchModal'
import { RowBetween } from '../Layout'
import NumericalInput from './NumericalInput'
import TokenAddressPanel from './TokenAddressPanel'

const SelectionRow = tw.div`
  flex px-16 pt-0 pb-16
`

const CurrencySelectButton = styled.button<{ selected: boolean }>`
  ${tw`py-0 px-12 rounded-12 text-primary h-48`}
  ${({ selected }) =>
    selected
      ? tw`bg-dark-gray-700 hover:bg-dark-gray-400 active:bg-dark-gray-300 border border-dark-gray-600 font-medium text-18`
      : tw`bg-blue hover:bg-blue-400 active:bg-blue-300 font-semibold text-16`};
`

const LabelRow = tw.div`
  flex flex-nowrap items-center text-primary text-14 leading-16 px-16 pt-16 pb-4
`

const SelectionPanel = tw.div`
  flex flex-col flex-nowrap relative z-[1]
`

const Container = tw.div`
  rounded-12 bg-dark-gray shadow-[0_2px_2px_-1px_rgba(74, 74, 104, 0.1)]
`

interface CurrencySelectionPanelProps {
  value: string
  onUserInput: (value: string) => void
  onMax?: () => void
  showMaxButton: boolean
  label?: string
  onCurrencySelect: (currency: Currency) => void
  currency?: Currency | null
  disableCurrencySelect?: boolean
  hideBalance?: boolean
  pair?: Pair | null
  hideInput?: boolean
  id: string
  showCommonBases?: boolean
}

export default function CurrencySelectionPanel({
  value,
  onUserInput,
  onMax,
  showMaxButton,
  label,
  onCurrencySelect,
  currency,
  disableCurrencySelect = false,
  hideBalance = false,
  pair = null, // used for double token logo
  hideInput = false,
  id,
  showCommonBases,
}: CurrencySelectionPanelProps) {
  const { account } = useWagmi()
  const selectedCurrencyBalance = useCurrencyBalance(account ?? undefined, currency ?? undefined)
  const { t } = useTranslation()
  const translatedLabel = label || t('Input')

  const [onPresentCurrencyModal] = useModal(
    <CurrencySearchModal
      onCurrencySelect={onCurrencySelect}
      selectedCurrency={currency}
      showCommonBases={showCommonBases}
    />,
  )
  return (
    <SelectionPanel id={id}>
      <Container>
        {!hideInput && (
          <LabelRow>
            <RowBetween>
              <Text fontSize="14px" color="placeholder">
                {translatedLabel}
              </Text>
              {account && (
                <div className="flex items-center gap-8">
                  <Text
                    onClick={onMax}
                    fontSize="14px"
                    style={{ display: 'inline', cursor: 'pointer' }}
                    color="placeholder"
                  >
                    {!hideBalance && !!currency
                      ? t('Balance: %balance%', { balance: selectedCurrencyBalance?.toSignificant(6) ?? t('Loading') })
                      : ' -'}
                  </Text>
                  {currency && showMaxButton && label !== 'To' && (
                    <SlButton className="x-small" variant="text" onClick={onMax}>
                      Max
                    </SlButton>
                  )}
                </div>
              )}
            </RowBetween>
          </LabelRow>
        )}
        <SelectionRow>
          <>
            <CurrencySelectButton
              selected={!!currency}
              className="open-currency-select-button"
              onClick={() => {
                if (!disableCurrencySelect) {
                  onPresentCurrencyModal()
                }
              }}
            >
              <div className="flex items-center justify-between notranslate" translate="no">
                {pair ? (
                  <CurrencyPairImage currencyA={pair.token0} currencyB={pair.token1} size="16px" className="mr-8" />
                ) : currency ? (
                  <CurrencyImage currency={currency} size="24px" className="mr-8" />
                ) : null}
                {pair
                  ? `${pair?.token0.symbol}:${pair?.token1.symbol}`
                  : (currency && currency.symbol && currency.symbol.length > 20
                      ? `${currency.symbol.slice(0, 4)}...${currency.symbol.slice(
                          currency.symbol.length - 5,
                          currency.symbol.length,
                        )}`
                      : currency?.symbol) || <span translate="yes">{t('Select a currency')}</span>}
                {!disableCurrencySelect && <ArrowDropDownIcon width="10px" ml="10px" color="white" />}
              </div>
            </CurrencySelectButton>
            {!hideInput && (
              <NumericalInput
                align="right"
                className="token-amount-input"
                value={value}
                onUserInput={(val) => {
                  onUserInput(val)
                }}
              />
            )}
          </>
        </SelectionRow>
        {currency && (
          <div className="px-16 pb-16">
            <TokenAddressPanel currency={currency} />
          </div>
        )}
      </Container>
    </SelectionPanel>
  )
}
